"use client";

import {
    useState,
    useCallback,
    ComponentProps,
    SyntheticEvent,
    useMemo,
    CSSProperties,
    FC,
    useEffect,
} from "react";
import Image from "next/image";
import classNames from "classnames";
import { usePrevious, useRuntimeConfig } from "@finbackoffice/site-core";
import { useParams } from "next/navigation";
import { formatAppNameText } from "@finbackoffice/fe-core";

type ImageProps = Omit<ComponentProps<typeof Image>, "src">;

export interface ImgProps extends ImageProps {
    source: string;
    alt: string;
    fallbackSrc?: string;
    wrapperClassName?: string;
    style?: CSSProperties;
    onFallback?: () => void;
    prefixAssetsUrl?: boolean;
    prefixAppName?: boolean;
}

const Img: FC<ImgProps> = ({
    source,
    fallbackSrc,
    wrapperClassName,
    alt,
    style,
    onFallback,
    prefixAssetsUrl = true,
    prefixAppName = false,
    ...rest
}) => {
    const COMMON_SITE_CONFIGS = useRuntimeConfig("COMMON_SITE_CONFIGS");
    const ASSETS_URL = useRuntimeConfig("ASSETS_URL");

    const { lang: locale } = useParams();

    const fullSrc = useMemo(() => {
        if (!source) return "";

        let src = source;

        if (prefixAppName) {
            src = `/${formatAppNameText(COMMON_SITE_CONFIGS.appName)}${src}`;
        }

        if (prefixAssetsUrl) {
            src = `${ASSETS_URL}${src}`;
        }

        return src;
    }, [source, prefixAppName, prefixAssetsUrl, COMMON_SITE_CONFIGS.appName, ASSETS_URL]);

    const [imgSrc, setImgSrc] = useState(fullSrc);
    const [loading, setLoading] = useState(true);
    const [isError, setIsError] = useState(false);

    const prevSource = usePrevious(fullSrc);

    const handleError = useCallback(() => {
        if (fallbackSrc) {
            if (imgSrc !== fallbackSrc) {
                setLoading(true);
                setImgSrc(fallbackSrc);
                onFallback?.();
            } else {
                setIsError(true);
                setLoading(false);
            }
        } else if (fullSrc.includes(locale as string)) {
            const localeFallback = fullSrc.replace(
                locale as string,
                COMMON_SITE_CONFIGS.defaultLocale,
            );

            if (imgSrc !== localeFallback) {
                setLoading(true);
                setImgSrc(localeFallback);
            } else {
                setIsError(true);
                setLoading(false);
            }
        } else {
            setIsError(true);
            setLoading(false);
        }
    }, [COMMON_SITE_CONFIGS.defaultLocale, fallbackSrc, fullSrc, imgSrc, locale, onFallback]);

    useEffect(() => {
        if (prevSource && prevSource !== fullSrc) {
            setLoading(true);
            setImgSrc(fullSrc);
        }
    }, [prevSource, fullSrc]);

    const handleOnLoad = (e: SyntheticEvent<HTMLImageElement>) => {
        setLoading(false);
        rest.onLoad?.(e);
    };

    const inlineStyles = useMemo(() => {
        const styles = { ...style, opacity: loading ? 0 : 1 };

        if (!rest.priority) {
            styles.transition = "opacity 0.2s";
        }

        return styles;
    }, [loading, rest.priority, style]);

    if (isError) {
        return null;
    }

    return (
        <span className={classNames("imageNext", wrapperClassName)}>
            <Image
                quality={100}
                alt={alt}
                src={imgSrc}
                onError={handleError}
                style={inlineStyles}
                {...rest}
                onLoad={handleOnLoad}
            />
        </span>
    );
};

export default Img;
